import ClientPortalLayout from '@/layouts/client-portal/client-portal-layout'
import PartnerPortalLayout from '@/layouts/partner-portal/partner-portal-layout'
import ClientAppointmentDetailPage from '@/pages/client-portal/client-appointment-detail-page'
import ClientAppointmentsPage from '@/pages/client-portal/client-appointments-page'
import ClientLoginPage from '@/pages/client-portal/client-login-page'
import ClientPublicOrderPage from '@/pages/client-portal/client-public-order-page'
import PublicHealthMapPage from '@/pages/client-portal/public-health-map-page'
import { BackdropLoading } from '@/components/backdrop-loading'
import { useIdleLogout } from '@/lib/hooks/use-idle-logout'
import { useSessionBootstrap } from '@/lib/hooks/use-session-bootstrap'
import { useReloadUser } from '@/lib/hooks/use-reload-user'
import SocketProvider from '@/lib/providers/socket-provider'
import BillingCheckoutPage from '@/pages/checkout/billing-checkout-page'
import PartnerAppointmentDetailPage from '@/pages/partner-portal/partner-appointment-detail-page'
import PartnerAppointmentsPage from '@/pages/partner-portal/partner-appointments-page'
import PartnerForgotCodePage from '@/pages/partner-portal/partner-forgot-code-page'
import PartnerLoginPage from '@/pages/partner-portal/partner-login-page'
import ConsentSignPage from '@/pages/public/consent/consent-sign-page'
import SharedFilePage from '@/pages/public/storage/shared-file-page'
import PatientTeleconsultationPage from '@/pages/teleconsultation/patient-teleconsultation-page'
import VerifySignaturePage from '@/pages/verify/verify-signature-page'
import { Navigate, Route, Routes as RouterRoutes, useLocation } from 'react-router-dom'
import { useAuth } from '../store/hooks'
import AdminRoutes from './admin-routes'
import MasterRoutes from './master-routes'
import PublicRoutes from './public-routes'

const Routes = () => {
    const { isAuthenticated, isMaster } = useAuth()

    // A sessão é recuperada do cookie httpOnly antes de qualquer tela pedir
    // dados: o access token vive só em memória e não sobrevive ao reload.
    const { ready } = useSessionBootstrap()

    useReloadUser()
    useIdleLogout()

    const location = useLocation()

    const pathname = location.pathname

    if (!ready) {
        return <BackdropLoading visible />
    }

    // Teleconsulta pública - sempre acessível, sem necessidade de login
    if (pathname.startsWith('/t/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/t/:token"
                    element={<PatientTeleconsultationPage />}
                />
            </RouterRoutes>
        )
    }

    // Checkout público de cobrança - acessível sem login
    if (pathname.startsWith('/pay/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/pay/:token"
                    element={<BillingCheckoutPage />}
                />
            </RouterRoutes>
        )
    }

    // Link público exclusivo de um procedimento flex - sem login, CPF confirma.
    // O paciente compartilha com quem for ajudar a pagar a cirurgia.
    if (pathname.startsWith('/p/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/p/:token"
                    element={<ClientPublicOrderPage />}
                />
            </RouterRoutes>
        )
    }

    // Mapa da Saúde respondido pelo paciente - sem login, CPF confirma.
    if (pathname.startsWith('/health-map/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/health-map/:token"
                    element={<PublicHealthMapPage />}
                />
            </RouterRoutes>
        )
    }

    // Arquivo compartilhado por link do Armazenamento - sem login
    if (pathname.startsWith('/compartilhado/')) {
        return (
            <RouterRoutes>
                <Route path="/compartilhado/:token" element={<SharedFilePage />} />
            </RouterRoutes>
        )
    }

    // Assinatura pública de consentimento
    if (pathname.startsWith('/medical/consents/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/medical/consents/sign/:token"
                    element={<ConsentSignPage />}
                />
            </RouterRoutes>
        )
    }

    // Portal do Parceiro - acessível sem login do sistema
    if (pathname.startsWith('/partner') && !pathname.startsWith('/person/partners')) {
        return (
            <RouterRoutes>
                <Route path="/partner" element={<PartnerLoginPage />} />
                <Route path="/partner/forgot-code" element={<PartnerForgotCodePage />} />
                <Route path="/partner" element={<PartnerPortalLayout />}>
                    <Route path="appointments" element={<PartnerAppointmentsPage />} />
                    <Route path="appointments/:type/:id" element={<PartnerAppointmentDetailPage />} />
                </Route>
            </RouterRoutes>
        )
    }

    // Área do cliente — sessão própria (userType CLIENT, a mesma conta do app).
    // O layout é quem barra: sem sessão manda para /client, e operador logado
    // volta para o painel.
    if (pathname.startsWith('/client')) {
        return (
            <RouterRoutes>
                <Route path="/client" element={<ClientLoginPage />} />
                <Route path="/client" element={<ClientPortalLayout />}>
                    <Route
                        path="appointments"
                        element={<ClientAppointmentsPage />}
                    />
                    <Route
                        path=":id"
                        element={<ClientAppointmentDetailPage />}
                    />
                </Route>
            </RouterRoutes>
        )
    }

    // Verificação pública de assinatura digital
    if (pathname.startsWith('/verify/')) {
        return (
            <RouterRoutes>
                <Route
                    path="/verify/:signatureId"
                    element={<VerifySignaturePage />}
                />
            </RouterRoutes>
        )
    }

    if (isAuthenticated && !isMaster) {
        return (
            <SocketProvider>
                <AdminRoutes />
            </SocketProvider>
        )
    }

    if (isAuthenticated && isMaster) {
        return (
            <SocketProvider>
                <MasterRoutes />
            </SocketProvider>
        )
    }

    const publicRoutes = [
        '/login',
        '/register',
        '/',
        '/auth/setup-password',
        '/auth/forgot-password',
        '/auth/reset-password',
    ]

    const isPublicRoute = publicRoutes.some(route => {
        if (route === '/') {
            return pathname === '/'
        }
        return pathname === route || pathname.startsWith(route + '/')
    })

    if (!isPublicRoute) {
        return <Navigate to="/" replace />
    }

    return <PublicRoutes />
}

export default Routes
